/**
 * /university/skills/$skillId — single skill view.
 *
 * Three-pane layout: skill header (name, key, status, version) →
 * effectiveness chart → tabs (Prompt | Examples | Versions). Founder &
 * tuner roles see action buttons: "Train" (start training session) and
 * "Export" (open ExportSkillDialog).
 */
import type { TrainingSessionSummary } from '@chaos/types/learning';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, Download, GitBranch, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { useToaster } from '../../../../components/ui/toast';
import { EffectivenessChart } from '../../../../components/university/effectiveness-chart';
import { ExportSkillDialog } from '../../../../components/university/export-skill-dialog';
import {
  effectivenessTone,
  fmtCount,
  fmtRelative,
  fmtScore,
  MARKETPLACE_STATUS_LABEL,
  TONE_DOT_CLASS,
  TONE_TEXT_CLASS,
} from '../../../../components/university/formatters';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

function SkillDetailRoute() {
  const { skillId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToaster();

  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canWrite = role === 'founder' || role === 'admin' || role === 'tuner';

  const skillQ = useQuery({
    queryKey: learningKeys.skill(skillId),
    queryFn: () => learningApi.getSkill(skillId),
  });
  const versionsQ = useQuery({
    queryKey: learningKeys.skillVersions(skillId),
    queryFn: () => learningApi.listSkillVersions(skillId),
    enabled: Boolean(skillId),
  });
  const effectivenessQ = useQuery({
    queryKey: learningKeys.effectiveness(skillId, skillQ.data?.version ?? ''),
    queryFn: () => learningApi.getEffectiveness(skillId, skillQ.data?.version ?? ''),
    enabled: Boolean(skillQ.data?.version),
    retry: false,
  });

  const createSessionM = useMutation({
    mutationFn: (initialDraft: string | undefined) =>
      learningApi.createTrainingSession({
        skillId,
        initialDraft,
        dryRun: false,
      }),
    onSuccess: (session: TrainingSessionSummary) => {
      qc.invalidateQueries({ queryKey: learningKeys.trainingSessions });
      toast({ title: 'Training session created', description: session.skillName ?? skillId });
      void navigate({
        to: '/university/training/$sessionId',
        params: { sessionId: session.id },
      });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not create session',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  if (skillQ.isPending) {
    return <div className="p-6 text-[12px] text-muted-foreground">Loading skill…</div>;
  }
  if (skillQ.isError || !skillQ.data) {
    return (
      <div className="p-6">
        <Link
          to="/university/skills"
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back
        </Link>
        <div className="mt-3 rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          {(skillQ.error as Error)?.message ?? 'Skill not found.'}
        </div>
      </div>
    );
  }

  const skill = skillQ.data;
  const tone = effectivenessTone(skill.effectivenessScore);
  const statusLabel = MARKETPLACE_STATUS_LABEL[skill.marketplaceStatus] ?? skill.marketplaceStatus;

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-6">
      <header className="flex flex-col gap-2">
        <Link
          to="/university/skills"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Skills
        </Link>
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
              />
              <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
                {skill.name}
              </h1>
              <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                v{skill.version}
              </span>
              <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {statusLabel}
              </span>
            </div>
            <span className="font-mono text-[12px] text-muted-foreground">{skill.key}</span>
            {skill.description ? (
              <p className="text-[13px] text-foreground/80">{skill.description}</p>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={cn('font-mono text-2xl font-semibold tabular-nums', TONE_TEXT_CLASS[tone])}
            >
              {fmtScore(skill.effectivenessScore)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {fmtCount(skill.totalAttempts)} attempts
            </span>
            {canWrite ? (
              <div className="mt-1 flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => createSessionM.mutate(skill.promptTemplate)}
                  disabled={createSessionM.isPending}
                >
                  {createSessionM.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  ) : (
                    <Wand2 className="h-3 w-3" aria-hidden />
                  )}
                  Train
                </Button>
                <ExportSkillDialog
                  skillId={skill.skillId}
                  skillName={skill.name}
                  skillKey={skill.key}
                  version={skill.version}
                  trigger={
                    <Button type="button" size="sm">
                      <Download className="h-3 w-3" aria-hidden /> Export
                    </Button>
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {effectivenessQ.data ? (
        <EffectivenessChart detail={effectivenessQ.data} />
      ) : effectivenessQ.isPending ? (
        <div className="rounded border border-border bg-background p-4 text-[12px] text-muted-foreground">
          Loading effectiveness…
        </div>
      ) : (
        <div className="rounded border border-border bg-background p-4 text-[12px] text-muted-foreground">
          Effectiveness data not available yet — needs at least 5 attempts.
        </div>
      )}

      <Tabs defaultValue="prompt" className="w-full">
        <TabsList>
          <TabsTrigger value="prompt">
            <Sparkles className="h-3 w-3" aria-hidden /> Prompt
          </TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="versions">
            <GitBranch className="h-3 w-3" aria-hidden /> Versions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prompt" className="flex flex-col gap-3 pt-4">
          {skill.systemPrompt ? (
            <PromptBlock title="System prompt" content={skill.systemPrompt} />
          ) : null}
          <PromptBlock title="Prompt template" content={skill.promptTemplate || '(empty)'} />
          {skill.antiPatterns.length > 0 ? (
            <div className="rounded border border-border bg-background p-3">
              <h3 className="mb-1 text-[12px] font-semibold text-foreground">Anti-patterns</h3>
              <ul className="ml-3 list-disc text-[12px] text-foreground/80">
                {skill.antiPatterns.map((ap) => (
                  <li key={ap}>{ap}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="examples" className="flex flex-col gap-3 pt-4">
          <ExamplesPair inputs={skill.exampleInputs} outputs={skill.exampleOutputs} />
        </TabsContent>

        <TabsContent value="versions" className="pt-4">
          {versionsQ.isPending ? (
            <div className="text-[12px] text-muted-foreground">Loading versions…</div>
          ) : (versionsQ.data ?? []).length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-muted-foreground/70">
              No version history.
            </div>
          ) : (
            <ol className="flex flex-col gap-2">
              {(versionsQ.data ?? []).map((v) => (
                <li
                  key={v.version}
                  className="flex items-center justify-between rounded border border-border bg-background px-3 py-2"
                >
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[12px] font-semibold text-foreground">
                        v{v.version}
                      </span>
                      {v.isActive ? (
                        <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-success">
                          Active
                        </span>
                      ) : null}
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {v.authoredByName ?? v.authoredByKind} · {fmtRelative(v.createdAt)}
                    </span>
                    {v.changelog ? (
                      <p className="mt-0.5 text-[11px] text-foreground/80">{v.changelog}</p>
                    ) : null}
                  </div>
                  <span
                    className={cn(
                      'font-mono text-[12px] tabular-nums',
                      TONE_TEXT_CLASS[effectivenessTone(v.effectivenessScore)],
                    )}
                  >
                    {fmtScore(v.effectivenessScore)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </TabsContent>
      </Tabs>
    </section>
  );
}

function PromptBlock({ title, content }: { title: string; content: string }) {
  return (
    <div className="flex flex-col gap-1 rounded border border-border bg-background p-3">
      <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground">{title}</h3>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-muted/30 p-2 font-mono text-[12px] text-foreground/90">
        {content}
      </pre>
    </div>
  );
}

function ExamplesPair({ inputs, outputs }: { inputs: unknown[]; outputs: unknown[] }) {
  if (inputs.length === 0 && outputs.length === 0) {
    return (
      <div className="px-2 py-3 text-[12px] text-muted-foreground/70">No examples recorded.</div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      <PromptBlock title="Example inputs" content={JSON.stringify(inputs, null, 2)} />
      <PromptBlock title="Example outputs" content={JSON.stringify(outputs, null, 2)} />
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/university/skills/$skillId')({
  component: SkillDetailRoute,
});
